import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Set in connectedCallback (see #onMqChange): the width at which this drawer stops being the nav.
   * @type {MediaQueryList | null}
   */
  #mq = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onEscapeDown, { capture: true });
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#adoptNativeOpen();
    // Powerlete's gender-tab drawer (.pw-mm) is the nav below 990px, not 750px
    // (blocks/powerlete-mobile-menu.liquid): close it only where the desktop mega menu takes over.
    this.#mq = window.matchMedia(this.matches('.pw-mm') ? '(min-width: 990px)' : '(min-width: 750px)');
    this.#mq.addEventListener('change', this.#onMqChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onEscapeDown, { capture: true });
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#mq?.removeEventListener('change', this.#onMqChange);
  }

  /**
   * The open <details> that Escape went down in, recorded before any inner handler runs.
   * @type {HTMLDetailsElement | null}
   */
  #escapeDisclosure = null;

  /**
   * @param {KeyboardEvent} event
   */
  #onEscapeDown = (event) => {
    if (event.key !== 'Escape') return;
    const details = event.target instanceof Element ? event.target.closest('details') : null;
    this.#escapeDisclosure = details?.open ? details : null;
  };

  /**
   * Powerlete patch (HORIZON-PATCHES.md, audit JS-07). The mobile drawer is desktop-nav's
   * hamburger only below 750px; at or above it the CSS that makes .menu-drawer visible no
   * longer applies, but nothing closed an OPEN <details>, so a phone rotated or a window
   * resized past 750px with the menu open left html[scroll-lock] set behind a drawer the
   * shopper could no longer see or reach: the page froze until they rotated back. Mirrors
   * powerlete-collection-filters.js's own _onMqChange, which resets its pane state the same
   * way on the same breakpoint.
   * Revised 2026-10-25 (Matt: keep the menu open across an unfold): for the Powerlete drawer the
   * breakpoint is 990px, where blocks/powerlete-mobile-menu.liquid hands over to the mega menu. At
   * 750-989px (tablets, an unfolded iPhone Duo at ~890px) the burger and drawer still show, so an
   * open menu stays open; crossing 990px still closes it, so the JS-07 freeze cannot return.
   * @param {MediaQueryListEvent} event
   */
  #onMqChange = (event) => {
    if (event.matches && this.isOpen) this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getEscapeTarget(event);
    this.#escapeDisclosure = null;
    if (details) this.#close(details);
  };

  /**
   * Powerlete patch (HORIZON-PATCHES.md, 2026-10-25, review R4-05). Escape closes
   * `event.target.closest('details')`, the innermost disclosure. In the Powerlete menu that was
   * usually an accordion group (`accordion-custom > details`): closing it hid the focused link,
   * dropped focus to <body> and left the drawer open. Accordion groups are page layout, not
   * something the shopper opened on top of the menu, so Escape now skips them and closes the
   * disclosure around them (the drawer itself, from the menu groups). The region sheet and the
   * search disclosure still close first, as before. A disclosure that was open when Escape went
   * down and is closed by keyup handled the key itself (the region sheet does, once its script has
   * loaded), so nothing more is closed.
   * @param {Event} event
   * @returns {HTMLDetailsElement | null}
   */
  #getEscapeTarget(event) {
    let details = this.#getDetailsElement(event);
    if (details === this.#escapeDisclosure && !details.open) return null;

    while (details !== this.refs.details && details.parentElement?.localName === 'accordion-custom') {
      const parent = details.parentElement.closest('details');
      if (!(parent instanceof HTMLDetailsElement) || !this.contains(parent)) return this.refs.details;
      details = parent;
    }
    return details;
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event] - the summary click
   */
  toggle(event) {
    // Powerlete patch (HORIZON-PATCHES.md, audit JS-01): this component alone sets [open]. The
    // summary's native toggle used to flip it too and raced #close(): with no transition to wait
    // for (reduced motion), reset() removed [open] first, the native toggle re-opened the
    // <details> without .menu-open, and dialog.js re-locked page scroll behind an invisible menu.
    // [open] still changes at the moment of the tap, in both directions, as the native toggle
    // did, so opening and closing look and time exactly as before.
    event?.preventDefault();
    const { details } = this.refs;

    if (this.isOpen) {
      this.close();
      details.open = false;
      return;
    }

    details.open = true;
    this.open();
  }

  /**
   * Powerlete patch (HORIZON-PATCHES.md, audit JS-01, revised 2026-10-24). A tap or key press on
   * the summary before this element was defined opens the <details> natively, without .menu-open.
   * The drawer's stylesheet shows that open (the `:not(:defined)` rules in
   * blocks/powerlete-mobile-menu.liquid), so finish it the way a scripted open would instead of
   * discarding the shopper's tap. .menu-open is added here, synchronously, so the drawer stays
   * visible in the frame where the `:not(:defined)` rules stop matching. An open made by this
   * component always sets aria-expanded="true" on the summary, so a real open (including the theme
   * editor's) is left alone.
   */
  #adoptNativeOpen() {
    const { details } = this.refs;
    if (!details.open || details.classList.contains('menu-open')) return;
    if (details.querySelector('summary')?.getAttribute('aria-expanded') === 'true') return;

    details.classList.add('menu-open');
    this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Powerlete patch (HORIZON-PATCHES.md, audit A11Y-02): captured before removeTrapFocus() runs
    // below, since its teardown/focusin listeners can move focus as elements are hidden/reset.
    const focusWasInside = details.contains(document.activeElement);

    // Powerlete patch (HORIZON-PATCHES.md, 2026-10-25, review R4-05): the same for a <details>
    // inside the drawer (the region sheet, the search disclosure, a Horizon submenu). Closing it
    // hides whatever had focus inside and the browser drops focus to <body>, so hand it to that
    // disclosure's own summary now. The drawer itself is A11Y-02's, in the callback below.
    if (details !== this.refs.details && focusWasInside && document.activeElement !== summary) {
      summary.focus({ preventScroll: true });
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // Powerlete patch (HORIZON-PATCHES.md, audit JS-01): this can resolve long after the close
        // (measured: ~2 s later, 10 ms after the menu was opened again). Resetting then stripped
        // [open] from the fresh open, so the menu showed with page scroll unlocked and the next tap
        // re-opened it. open() sets aria-expanded="true" synchronously, so it marks a re-open.
        if (summary.getAttribute('aria-expanded') === 'true') return;

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          // Powerlete patch (audit A11Y-02): closing the main drawer left focus wherever
          // removeTrapFocus()'s teardown dropped it (typically <body>), so keyboard/switch users
          // lost their place (WCAG 2.4.3) -- restore it to the trigger. Only when focus was
          // genuinely inside the drawer: a programmatic close (e.g. resize) shouldn't steal focus.
          if (focusWasInside) summary.focus();
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
